import { useEffect, useMemo, useState } from "react";
import { useApp } from "@/store/AppStore";
import SectionTitle from "@/components/base/SectionTitle";
import Modal from "@/components/base/Modal";
import { ghostButtonClass } from "@/components/base/fieldStyles";
import { MONTHS } from "@/utils/format";
import ClientStats from "@/pages/admin/clientes/components/ClientStats";
import ClientTable from "@/pages/admin/clientes/components/ClientTable";
import ClientFilters from "@/pages/admin/clientes/components/ClientFilters";
import ClientFormModal from "@/pages/admin/clientes/components/ClientFormModal";
import ClientDetailModal from "@/pages/admin/clientes/components/ClientDetailModal";
import PaymentModal from "@/pages/admin/cartera/components/PaymentModal";
import ReceivableFormModal from "@/pages/admin/cartera/components/ReceivableFormModal";
import type { Client, Receivable } from "@/types";

const monthKeyOf = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabelOf = (key: string): string => {
  const [year, month] = key.split("-");
  const idx = Number(month) - 1;
  return `${MONTHS[idx] ?? month} ${year}`;
};

const ClientsPage = () => {
  const {
    data,
    addClient,
    updateClient,
    deleteClient,
    deleteClients,
    addReceivable,
    updateReceivable,
  } = useApp();
  const [query, setQuery] = useState("");
  const [month, setMonth] = useState("");
  const [origin, setOrigin] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);
  const [deleting, setDeleting] = useState<Client | null>(null);
  const [detail, setDetail] = useState<Client | null>(null);
  const [payTarget, setPayTarget] = useState<Receivable | null>(null);
  const [fiadoFor, setFiadoFor] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [feedback, setFeedback] = useState("");

  const monthOptions = useMemo(() => {
    const set = new Set<string>();
    data.sales.forEach((s) => {
      const key = monthKeyOf(s.date);
      if (key) set.add(key);
    });
    return Array.from(set)
      .sort((a, b) => b.localeCompare(a))
      .map((value) => ({ value, label: monthLabelOf(value) }));
  }, [data.sales]);

  const originOptions = useMemo(() => {
    const set = new Set<string>();
    data.sales.forEach((s) => {
      if (s.origin) set.add(s.origin);
    });
    return Array.from(set).sort();
  }, [data.sales]);

  const scopedSales = useMemo(() => {
    if (!month && !origin) return data.sales;
    return data.sales.filter((s) => {
      if (month && monthKeyOf(s.date) !== month) return false;
      if (origin && s.origin !== origin) return false;
      return true;
    });
  }, [data.sales, month, origin]);

  const filteredClients = useMemo(() => {
    if (!month && !origin) return data.clients;
    const keys = new Set<string>();
    scopedSales.forEach((s) => {
      if (s.clientId) keys.add(s.clientId);
      if (s.clientName) keys.add(s.clientName.trim().toLowerCase());
    });
    return data.clients.filter(
      (c) => (c.id && keys.has(c.id)) || keys.has(c.name.trim().toLowerCase())
    );
  }, [data.clients, scopedSales, month, origin]);

  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return filteredClients;
    return filteredClients.filter((c) =>
      [c.name, c.cedula, c.phone, c.city, c.department, c.neighborhood, c.address].some((v) =>
        (v ?? "").toLowerCase().includes(q)
      )
    );
  }, [filteredClients, query]);

  const clearFilters = () => {
    setMonth("");
    setOrigin("");
  };

  // Depura la selección cuando cambia el conjunto visible (filtros/búsqueda).
  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => searched.some((c) => c.id === id)));
  }, [searched]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const allSelected = searched.length > 0 && searched.every((c) => prev.includes(c.id));
      return allSelected ? [] : searched.map((c) => c.id);
    });
  };

  const confirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const count = selectedIds.length;
    await deleteClients(selectedIds);
    setSelectedIds([]);
    setBulkOpen(false);
    setFeedback(`${count} cliente${count === 1 ? "" : "s"} eliminado${count === 1 ? "" : "s"}.`);
    setTimeout(() => setFeedback(""), 2200);
  };

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (client: Client) => {
    setEditing(client);
    setFormOpen(true);
  };

  const handleSubmit = async (client: Client) => {
    if (editing) {
      await updateClient(client);
      setFeedback("Cliente actualizado.");
    } else {
      await addClient(client);
      setFeedback("Cliente creado.");
    }
    setFormOpen(false);
    setTimeout(() => setFeedback(""), 2200);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    await deleteClient(deleting.id);
    setDeleting(null);
    setFeedback("Cliente eliminado.");
    setTimeout(() => setFeedback(""), 2200);
  };

  const notify = (msg: string) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(""), 2400);
  };

  // Registra un abono en la cartera asociada al cliente (baja el saldo).
  const handleClientPayment = async (r: Receivable) => {
    await updateReceivable(r);
    setPayTarget(null);
    notify("Abono registrado. La cartera se actualizó.");
  };

  // Crea un nuevo fiado (cuenta por cobrar) para el cliente desde su ficha.
  const handleCreateFiado = async (r: Receivable) => {
    await addReceivable(r);
    setFiadoFor(null);
    notify("Fiado registrado en la cartera.");
  };

  return (
    <div className="pb-24">
      <SectionTitle
        text="Clientes"
        subtitle="Tu base de clientes: datos de contacto, ubicación e historial de compras. Filtra por mes de compra o por origen de captación y aquí aterrizan los clientes que cargues desde el respaldo de Excel."
      />

      <ClientStats clients={searched} sales={scopedSales} />

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mt-5 mb-4">
        <div className="relative flex-1 max-w-md">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-accent-300">
            <i className="ri-search-line" />
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre, cédula, celular o ciudad..."
            className="w-full bg-background-100/70 border border-background-300/50 rounded-md pl-9 pr-3 py-2 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/50 focus:ring-2 focus:ring-accent-500/25"
            aria-label="Buscar cliente"
          />
        </div>

        <button
          onClick={openNew}
          className="px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2 lg:ml-auto"
        >
          <i className="ri-user-add-line" /> Nuevo cliente
        </button>
      </div>

      <ClientFilters
        month={month}
        origin={origin}
        months={monthOptions}
        origins={originOptions}
        resultCount={searched.length}
        onMonth={setMonth}
        onOrigin={setOrigin}
        onClear={clearFilters}
      />

      {feedback && (
        <div className="mb-3 rounded-md glass-panel border-accent-500/30 px-4 py-2.5 flex items-center gap-2 animate-fade-up">
          <i className="ri-checkbox-circle-line text-accent-300" />
          <p className="text-xs text-foreground-800">{feedback}</p>
        </div>
      )}

      {selectedIds.length > 0 && (
        <div className="mb-3 rounded-md glass-panel border-primary-500/40 px-4 py-2.5 flex flex-wrap items-center gap-3 animate-fade-up">
          <p className="text-xs text-foreground-800 flex items-center gap-2">
            <i className="ri-checkbox-multiple-line text-primary-200" />
            <strong className="text-foreground-950">{selectedIds.length}</strong> seleccionado
            {selectedIds.length === 1 ? "" : "s"}
          </p>
          <div className="flex items-center gap-2 sm:ml-auto">
            <button
              onClick={() => setSelectedIds([])}
              className="px-3 py-2 rounded-md border border-background-400/40 text-foreground-700 hover:bg-background-200/40 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-1.5"
            >
              <i className="ri-close-line" /> Limpiar selección
            </button>
            <button
              onClick={() => setBulkOpen(true)}
              className="px-3 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-1.5"
            >
              <i className="ri-delete-bin-line" /> Eliminar seleccionados
            </button>
          </div>
        </div>
      )}

      <ClientTable
        clients={searched}
        sales={scopedSales}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleAll={toggleAll}
        onView={setDetail}
        onEdit={openEdit}
        onDelete={setDeleting}
      />

      <ClientFormModal
        open={formOpen}
        client={editing}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />

      <ClientDetailModal
        open={detail !== null}
        client={detail}
        sales={data.sales}
        receivables={data.receivables}
        onClose={() => setDetail(null)}
        onPay={setPayTarget}
        onNewFiado={(clientName) => {
          setDetail(null);
          setFiadoFor(clientName);
        }}
      />

      <PaymentModal
        open={payTarget !== null}
        receivable={payTarget}
        onClose={() => setPayTarget(null)}
        onSubmit={handleClientPayment}
      />

      <ReceivableFormModal
        open={fiadoFor !== null}
        onClose={() => setFiadoFor(null)}
        onSubmit={handleCreateFiado}
        clients={data.clients}
        products={data.products}
        inventory={data.inventory}
        defaultClientName={fiadoFor ?? ""}
      />

      <Modal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Eliminar cliente"
        icon="ri-delete-bin-line"
        size="sm"
        footer={
          <>
            <button onClick={() => setDeleting(null)} className={ghostButtonClass}>
              Cancelar
            </button>
            <button
              onClick={confirmDelete}
              className="px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
            >
              <i className="ri-delete-bin-line" /> Eliminar
            </button>
          </>
        }
      >
        <p className="text-sm text-foreground-700">
          ¿Seguro que quieres eliminar a{" "}
          <strong className="text-foreground-950">{deleting?.name}</strong>? Se borrará su registro
          de cliente (sus ventas registradas se conservan).
        </p>
      </Modal>

      <Modal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Eliminar clientes"
        icon="ri-delete-bin-6-line"
        size="sm"
        footer={
          <>
            <button onClick={() => setBulkOpen(false)} className={ghostButtonClass}>
              Cancelar
            </button>
            <button
              onClick={confirmBulkDelete}
              className="px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
            >
              <i className="ri-delete-bin-line" /> Eliminar {selectedIds.length}
            </button>
          </>
        }
      >
        <p className="text-sm text-foreground-700">
          ¿Seguro que quieres eliminar{" "}
          <strong className="text-foreground-950">{selectedIds.length}</strong> cliente
          {selectedIds.length === 1 ? "" : "s"} de una sola vez? Se borrarán sus registros (sus
          ventas registradas se conservan).
        </p>
      </Modal>
    </div>
  );
};

export default ClientsPage;